import React from 'react'
import { Circle } from 'rc-progress'
import iconCheck from '../../../../../../images/iconCheck.png'
import iconOver from '../../../../../../images/iconOver.png'
import iconUnder from '../../../../../../images/iconUnder.png'

/*
The average macro graph component for check-in forms, timeline table, average macro intake component. 
Not to be confused with the interactible tracker macro graph.

Protein Range
  Target <=100: +- 10
  Target >100 && < 250: Math.round((1/30 * proteinTarget) + (6.6666666667))
  Target >=250: +- 15

Carbs Range
  Target <=50: +- 5
  Target >50 && <500: Math.round((11/450 * carbsTarget) + (3.7777777778))
  Target >=500: +- 16

Fat Range
  Target <=25: +-4
  Target >25 && <175: +- Math.round((2/75 * fatTarget) + (10/3))
  Target >=175: +-8
*/

const colorMap = {
  'under': '#31bfff',
  'in-range': '#00ee46',
  'over': '#ed28a5'
}

const MacroGraph = ({
  proteinConsumed,
  carbsConsumed,
  fatConsumed,
  proteinTarget,
  carbsTarget,
  fatTarget,
  vacationMode,
  showConsumedMacros,
  setShowConsumedMacros,
  showUnderRange, //show under range label (for timeline preview)
  light //light or dark mode
}) => {
  
  const renderMap = {
    'under': showUnderRange ? <p className="tracker-range-label" style={{ color: light ? "#060a27":"#FFF" }}><img src={iconUnder} alt="alert" /> UNDER RANGE</p> : <></>,
    'in-range': <p className="tracker-range-label" style={{ color: light ? "#060a27":"#FFF" }}><img src={iconCheck} alt="check" /> IN RANGE</p>,
    'over': <p className="tracker-range-label" style={{ color: light ? "#060a27":"#FFF" }}><img src={iconOver} alt="alert" /> OVER RANGE</p>
  };
  

  const proteinSlope = Math.round((1 / 30) * proteinTarget + 6.6666666667);
  const carbsSlope = Math.round((11 / 450) * carbsTarget + 3.7777777778);
  const fatSlope = Math.round((2 / 75) * fatTarget + 10 / 3);
  const remainingProtein = Math.round(proteinTarget - proteinConsumed);
  const remainingCarbs = Math.round(carbsTarget - carbsConsumed);
  const remainingFat = Math.round(fatTarget - fatConsumed);
  let proteinPercent = 0;
  let carbsPercent = 0;
  let fatPercent = 0;
  let proteinAngle = 0;
  let carbsAngle = 0;
  let fatAngle = 0;
  let proteinStatus = "under";
  let carbsStatus = "under";
  let fatStatus = "under";

  if (proteinTarget > 0 && proteinTarget <= 100) {
    proteinPercent = (proteinConsumed / (proteinTarget + 10)) * 100;
    proteinAngle = ((proteinTarget - 10) / (proteinTarget + 10)) * 360;
    proteinStatus = remainingProtein < -10 ? "over" : remainingProtein >= -10 && remainingProtein <= 10 ? "in-range" : "under";
  } else if (proteinTarget > 100 && proteinTarget < 250) {
    proteinPercent = (proteinConsumed / (proteinTarget + proteinSlope)) * 100;
    proteinAngle = ((proteinTarget - proteinSlope) / (proteinTarget + proteinSlope)) * 360;
    proteinStatus = remainingProtein < -proteinSlope ? "over" : remainingProtein >= -proteinSlope && remainingProtein <= proteinSlope ? "in-range" : "under";
  } else {
    proteinPercent = (proteinConsumed / (proteinTarget + 15)) * 100;
    proteinAngle = ((proteinTarget - 15) / (proteinTarget + 15)) * 360;
    proteinStatus = remainingProtein < -15 ? "over" : remainingProtein >= -15 && remainingProtein <= 15 ? "in-range" : "under";
  }

  if (carbsTarget > 0 && carbsTarget <= 50) {
    carbsPercent = (carbsConsumed / (carbsTarget + 5)) * 100;
    carbsAngle = ((carbsTarget - 5) / (carbsTarget + 5)) * 360;
    carbsStatus = remainingCarbs < -5 ? "over" : remainingCarbs >= -5 && remainingCarbs <= 5 ? "in-range" : "under";
  } else if (carbsTarget > 50 && carbsTarget < 500) {
    carbsPercent = (carbsConsumed / (carbsTarget + carbsSlope)) * 100;
    carbsAngle = ((carbsTarget - carbsSlope) / (carbsTarget + carbsSlope)) * 360;
    carbsStatus = remainingCarbs < -carbsSlope ? "over" : remainingCarbs >= -carbsSlope && remainingCarbs <= carbsSlope ? "in-range" : "under";
  } else {
    carbsPercent = (carbsConsumed / (carbsTarget + 16)) * 100;
    carbsAngle = ((carbsTarget - 16) / (carbsTarget + 16)) * 360;
    carbsStatus = remainingCarbs < -16 ? "over" : remainingCarbs >= -16 && remainingCarbs <= 16 ? "in-range" : "under";
  }

  if (fatTarget > 0 && fatTarget <= 25) {
    fatPercent = (fatConsumed / (fatTarget + 4)) * 100;
    fatAngle = ((fatTarget - 4) / (fatTarget + 4)) * 360;
    fatStatus = remainingFat < -4 ? "over" : remainingFat >= -4 && remainingFat <= 4 ? "in-range" : "under";
  } else if (fatTarget > 25 && fatTarget < 175) {
    fatPercent = (fatConsumed / (fatTarget + fatSlope)) * 100;
    fatAngle = ((fatTarget - fatSlope) / (fatTarget + fatSlope)) * 360;
    fatStatus = remainingFat < -fatSlope ? "over" : remainingFat >= -fatSlope && remainingFat <= fatSlope ? "in-range" : "under";
  } else {
    fatPercent = (fatConsumed / (fatTarget + 8)) * 100;
    fatAngle = ((fatTarget - 8) / (fatTarget + 8)) * 360;
    fatStatus = remainingFat < -8 ? "over" : remainingFat >= -8 && remainingFat <= 8 ? "in-range" : "under";
  }

  return vacationMode === 2 ? (
    <>
      {setShowConsumedMacros && (
        <div className="tracker-macro-count-container">
          <button className="active">Consumed</button>
        </div>
      )}
      <div className="tracker-graph-container" key="back">
        <div className="circle-container" style={{ height: '165px' }}>
          <div className="tracker-graph-data">
            <div className="tracker-graph-macros">
              <h6 style={{ color: light ? "#060a27":"#FFF" }}>Avg Protein</h6>
              <h4 style={{ color: light ? "#060a27":"#FFF" }}>
                {proteinConsumed}
              </h4>
            </div>
          </div>
        </div>
        <div className="circle-container circle-center" style={{ height: '165px' }}>
          <div className="tracker-graph-data">
            <div className="tracker-graph-macros">
              <h6 style={{ color: light ? "#060a27":"#FFF" }}>Avg Carbs</h6>
              <h4 style={{ color: light ? "#060a27":"#FFF" }}>
                {carbsConsumed}
              </h4>
            </div>
          </div>
        </div>
        <div className="circle-container">
          <div className="tracker-graph-data">
            <div className="tracker-graph-macros">
              <h6 style={{ color: light ? "#060a27":"#FFF" }}>
                Avg
                <br />
                Fat
              </h6>
              <h4 style={{ color: light ? "#060a27":"#FFF" }}>
                {fatConsumed}
              </h4>
            </div>
          </div>
        </div>
      </div>
    </>
  ) : (
    <>
      {setShowConsumedMacros && (
        <div className="tracker-macro-count-container">
          <button
            className={showConsumedMacros ? "active" : ""}
            onClick={() => setShowConsumedMacros(true)}
          >
            Consumed
          </button>
          <button
            className={showConsumedMacros ? "" : "active"}
            onClick={() => setShowConsumedMacros(false)}
          >
            Remaining
          </button>
        </div>
      )}
      <div className="tracker-graph-container" key="back">
        <div className="circle-container">
          {renderMap[proteinStatus]}
          <Circle
            percent={proteinPercent}
            strokeWidth="5"
            strokeLinecap="round"
            strokeColor={colorMap[proteinStatus]}
            trailWidth="5"
            trailColor={light ? "#ecf0f9" : "#1d2345"}
          />
          <div className="tracker-graph-data">
            <div className="tracker-graph-macros">
              <h6 style={{ color: light ? "#060a27":"#FFF" }}>Avg Protein</h6>
              <h4 style={{ color: light ? "#060a27":"#FFF" }}>
                {showConsumedMacros ? proteinConsumed : remainingProtein}
              </h4>
              <h6 style={{ color: light ? "#060a27":"#FFF" }}>of {proteinTarget}</h6>
            </div>
          </div>
          <div className="circle-graph-indicator-middle">
            {(proteinStatus === "under" && !showUnderRange) && <p>MAX</p>}
            <div className="circle-graph-bar-white" style={{ background: light ? "#6a6a6a":"#FFF" }}></div>
          </div>
          <div
            style={{ transform: `translateX(-50%) rotate(${proteinAngle}deg)` }}
            className={
              !!proteinTarget
                ? "circle-graph-indicator"
                : "circle-graph-indicator-none"
            }
          >
            {(proteinStatus === "under" && !showUnderRange) && (
              <p style={{transform: `translateX(${ proteinAngle < 300 ? "-10" : "-50"}%) translateY(-50%) rotate(${360 - proteinAngle}deg)`}}>
                MIN
              </p>
            )}
            <div className="circle-graph-bar-white" style={{ background: light ? "#6a6a6a":"#FFF" }}></div>
          </div>
        </div>
        <div className="circle-container circle-center">
          {renderMap[carbsStatus]}
          <Circle
            percent={carbsPercent}
            strokeWidth="5"
            strokeLinecap="round"
            strokeColor={colorMap[carbsStatus]}
            trailWidth="5"
            trailColor={light ? "#ecf0f9" : "#1d2345"}
          />
          <div className="tracker-graph-data">
            <div className="tracker-graph-macros">
              <h6 style={{ color: light ? "#060a27":"#FFF" }}>Avg Carbs</h6>
              <h4 style={{ color: light ? "#060a27":"#FFF" }}>
                {showConsumedMacros ? carbsConsumed : remainingCarbs}
              </h4>
              <h6 style={{ color: light ? "#060a27":"#FFF" }}>of {carbsTarget}</h6>
            </div>
          </div>
          <div className="circle-graph-indicator-middle">
            {(carbsStatus === "under" && !showUnderRange) && <p>MAX</p>}
            <div className="circle-graph-bar-white" style={{ background: light ? "#6a6a6a":"#FFF" }}></div>
          </div>
          <div
            style={{ transform: `translateX(-50%) rotate(${carbsAngle}deg)` }}
            className={!!carbsTarget? "circle-graph-indicator" : "circle-graph-indicator-none"}
          >
            {(carbsStatus === "under" && !showUnderRange) && (
              <p style={{transform: `translateX(${carbsAngle < 300 ? "-10" : "-50"}%) translateY(-50%) rotate(${360 - carbsAngle}deg)`,}}>
                MIN
              </p>
            )}
            <div className="circle-graph-bar-white" style={{ background: light ? "#6a6a6a":"#FFF" }}></div>
          </div>
        </div>
        <div className="circle-container">
          {renderMap[fatStatus]}
          <Circle
            percent={fatPercent}
            strokeWidth="5"
            strokeLinecap="round"
            strokeColor={colorMap[fatStatus]}
            trailWidth="5"
            trailColor={light ? "#ecf0f9" : "#1d2345"}
          />
          <div className="tracker-graph-data">
            <div className="tracker-graph-macros">
              <h6 style={{ color: light ? "#060a27":"#FFF" }}>
                Avg
                <br />
                Fat
              </h6>
              <h4 style={{ color: light ? "#060a27":"#FFF" }}>
                {showConsumedMacros ? fatConsumed : remainingFat}
              </h4>
              <h6 style={{ color: light ? "#060a27":"#FFF" }}>of {fatTarget}</h6>
            </div>
          </div>
          <div className="circle-graph-indicator-middle">
            {(fatStatus === "under" && !showUnderRange) && <p>MAX</p>}
            <div className="circle-graph-bar-white" style={{ background: light ? "#6a6a6a":"#FFF" }}></div>
          </div>
          <div
            style={{ transform: `translateX(-50%) rotate(${fatAngle}deg)` }}
            className={!!fatTarget ? "circle-graph-indicator" : "circle-graph-indicator-none"}
          >
            {(fatStatus ===  "under" && !showUnderRange) && (
              <p style={{transform: `translateX(${fatAngle < 300 ? "-10" : "-50"}%) translateY(-50%) rotate(${360 - fatAngle}deg)`}}>
                MIN
              </p>
            )}
            <div className="circle-graph-bar-white" style={{ background: light ? "#6a6a6a":"#FFF" }}></div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MacroGraph

